<template>
    <div>
        <div v-if="loading" class="rr-loading"><v-progress-circular indeterminate color="primary" size="26" width="3" /></div>
        <div v-else-if="!rows.length" class="rr-empty">No brand data for the selected filters.</div>
        <table v-else class="rr-table">
            <thead>
                <tr>
                    <th>Name</th>
                    <th>Rank</th>
                    <th>Visibility (%)</th>
                    <th>Sentiment</th>
                    <th>Mentions</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="row in rows" :key="row.canonicalName" :class="{ 'rr-row--primary': row.isPrimary }">
                    <td class="rr-brand-cell">
                        <span class="rr-brand-dot" :style="{ background: row.color }"></span>
                        {{ row.canonicalName }}
                    </td>
                    <td>
                        <span class="rr-rank-plain">#{{ row.rank }}</span>
                        <span v-if="row.rankDelta != null" class="rr-delta-chip" :class="{ up: row.rankDelta > 0, down: row.rankDelta < 0 }">
                            <v-icon x-small>{{ row.rankDelta > 0 ? "mdi-arrow-up" : row.rankDelta < 0 ? "mdi-arrow-down" : "mdi-minus" }}</v-icon>
                            {{ row.rankDelta > 0 ? "+" + row.rankDelta : row.rankDelta }}
                        </span>
                    </td>
                    <td>
                        {{ row.vis.toFixed(2) }}%
                        <span v-if="row.visDelta != null" class="rr-delta-chip" :class="{ up: row.visDelta > 0, down: row.visDelta < 0 }">
                            <v-icon x-small>{{ row.visDelta > 0 ? "mdi-arrow-up" : row.visDelta < 0 ? "mdi-arrow-down" : "mdi-minus" }}</v-icon>
                            {{ (row.visDelta > 0 ? "+" : "") + row.visDelta.toFixed(2) }}%
                        </span>
                    </td>
                    <td>
                        {{ row.sentText }}
                        <span v-if="row.sentDelta != null" class="rr-delta-chip" :class="{ up: row.sentDelta > 0, down: row.sentDelta < 0 }">
                            <v-icon x-small>{{ row.sentDelta > 0 ? "mdi-arrow-up" : row.sentDelta < 0 ? "mdi-arrow-down" : "mdi-minus" }}</v-icon>
                            {{ (row.sentDelta > 0 ? "+" : "") + row.sentDelta.toFixed(0) }}
                        </span>
                    </td>
                    <td>
                        {{ row.mentions }}
                        <span v-if="row.mentionsDelta != null" class="rr-delta-chip" :class="{ up: row.mentionsDelta > 0, down: row.mentionsDelta < 0 }">
                            <v-icon x-small>{{ row.mentionsDelta > 0 ? "mdi-arrow-up" : row.mentionsDelta < 0 ? "mdi-arrow-down" : "mdi-minus" }}</v-icon>
                            {{ (row.mentionsDelta > 0 ? "+" : "") + row.mentionsDelta }}
                        </span>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<script>
    import { fetchRangeAverages, computeRanges, isPrimaryBrand } from "../reportApi"
    import { resolveBrandColor } from "../../../utils/brandColor"

    export default {
        name: "BrandTableSection",
        props: {
            filters: { type: Object, required: true },
            companyId: { type: String, required: true },
            projectId: { type: String, required: true },
            project: { type: Object, default: null },
            brands: { type: Array, default: () => [] }
        },
        data() {
            return { loading: true, rows: [] }
        },
        methods: {
            async fetchAll() {
                this.loading = true
                try {
                    const ranges = computeRanges(this.filters.selectedDates)
                    if (!ranges) {
                        this.rows = []
                        return
                    }
                    const data = await fetchRangeAverages(this.companyId, {
                        projectId: this.projectId,
                        platform: this.filters.platform && this.filters.platform !== "all" ? this.filters.platform : null,
                        ranges,
                        brands: this.brands,
                        tags: this.filters.tags,
                        tagsOperator: this.filters.tagsOperator
                    })
                    const valid = data.filter((b) => typeof b.visibilityPercent === "number" && b.visibilityPercent > 0)
                    const sorted = [...valid].sort((a, b) => (b.visibilityPercent || 0) - (a.visibilityPercent || 0))
                    const prevSorted = [...valid]
                        .filter((b) => typeof b.visibilityPercentOld === "number")
                        .sort((a, b) => (b.visibilityPercentOld || 0) - (a.visibilityPercentOld || 0))
                    const brandColors = this.project?.ai_brand_colors || {}
                    this.rows = sorted.slice(0, 10).map((b, i) => {
                        const prevRank = prevSorted.findIndex((p) => p === b)
                        const sent = typeof b.sentimentAvg === "number" ? b.sentimentAvg * 100 : null
                        const sentOld = typeof b.sentimentAvgOld === "number" ? b.sentimentAvgOld * 100 : null
                        return {
                            canonicalName: b.canonicalName || b.brand_name,
                            color: resolveBrandColor(b.canonicalName || b.brand_name || "", brandColors),
                            rank: i + 1,
                            rankDelta: prevRank > -1 ? prevRank - i : null,
                            vis: b.visibilityPercent || 0,
                            visDelta: typeof b.visibilityPercentOld === "number" ? (b.visibilityPercent || 0) - b.visibilityPercentOld : null,
                            sentText: sent != null ? (sent > 0 ? "+" : "") + sent.toFixed(0) : "—",
                            sentDelta: sent != null && sentOld != null ? sent - sentOld : null,
                            mentions: b.responsesWithMention || 0,
                            mentionsDelta: typeof b.responsesWithMentionOld === "number" ? (b.responsesWithMention || 0) - b.responsesWithMentionOld : null,
                            isPrimary: isPrimaryBrand(b, this.project)
                        }
                    })
                } catch (e) {
                    this.rows = []
                } finally {
                    this.loading = false
                    this.$emit("ready")
                }
            }
        },
        mounted() {
            this.fetchAll()
        }
    }
</script>
